import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Plus, Search, MapPin, Calendar as CalendarIcon, Trash2, Pencil, Kanban, List, CalendarPlus, Inbox, ClipboardList, Target, Lightbulb, MessageSquare, Building2, ExternalLink as ExternalLinkIcon } from "lucide-react";
import { buildGoogleCalendarUrl } from "@/lib/gcal";
import { DEMAND_SOURCES, EMPTY_EVENT } from "@/lib/eventConstants";
import AutocompleteInput from "@/components/AutocompleteInput";
import GooglePlacePicker from "@/components/GooglePlacePicker";

const PHASES = [
  { v: "commercialisation", label: "Commercialisation",
    color: "bg-amber-500/15 text-amber-200 border-amber-400",
    colStyle: "bg-amber-500/15/60 border-amber-500/50",
    accent: "#d97706" },
  { v: "production", label: "Production",
    color: "bg-emerald-500/15 text-emerald-200 border-emerald-500",
    colStyle: "bg-emerald-500/15/60 border-emerald-500/50",
    accent: "#059669" },
  { v: "postprod", label: "Post-production",
    color: "bg-sky-500/15 text-sky-200 border-sky-400",
    colStyle: "bg-sky-500/15/60 border-sky-500/50",
    accent: "#0284c7" },
  { v: "cancelled", label: "Événement annulé",
    color: "bg-red-500/15 text-red-200 border-red-400",
    colStyle: "bg-red-500/15/60 border-red-500/50",
    accent: "#dc2626" },
  { v: "archived", label: "Archivé",
    color: "bg-muted text-muted-foreground border-border",
    colStyle: "bg-muted border-border",
    accent: "#6b7280" },
];

// Statut détaillé auto (dérivé côté backend) → couleur de pastille
const STATUS_STYLE = {
  // Commercialisation (jaune ambre)
  devis_a_faire:    "bg-amber-500/15 text-amber-200 border-amber-500/50",
  devis_a_valider:  "bg-amber-500/15 text-amber-200 border-amber-400",
  devis_a_envoyer:  "bg-amber-200 text-amber-200 border-amber-500",
  devis_envoye:     "bg-amber-300 text-amber-950 border-amber-600",
  devis_valide:     "bg-amber-400 text-amber-950 border-amber-700 font-bold",
  // Production (vert)
  fdr_a_faire:      "bg-emerald-500/15 text-emerald-200 border-emerald-500/50",
  fdr_a_valider:    "bg-emerald-500/15 text-emerald-200 border-emerald-400",
  fdr_a_envoyer:    "bg-emerald-200 text-emerald-200 border-emerald-500",
  fdr_envoyee:      "bg-emerald-400 text-emerald-950 border-emerald-700 font-bold",
  // Post-prod (bleu)
  facture_a_envoyer: "bg-sky-500/15 text-sky-200 border-sky-500/50",
  facture_envoyee:   "bg-sky-200 text-sky-200 border-sky-500",
  facture_payee:     "bg-sky-400 text-sky-950 border-sky-700 font-bold",
  // Annulé (rouge)
  proposition_refusee: "bg-red-500/15 text-red-200 border-red-400 font-bold",
  archived: "bg-muted text-muted-foreground border-border",
};

function EventCard({ ev, onOpen, onDragStart }) {
  const phaseInfo = PHASES.find(p => p.v === ev.phase) || PHASES[0];
  const accent = phaseInfo.accent;
  const isCancelled = ev.phase === "cancelled";
  const refusedAt = ev.client_offer?.refused_at || "";
  const refusedReason = ev.client_offer?.refused_reason || "";
  const isCommPhase = ev.phase === "commercialisation" || ev.phase === "cancelled";
  const leadName = isCommPhase ? (ev.commercial_lead_name || ev.referent_name) : (ev.project_lead_name || ev.referent_name);
  const leadLabel = isCommPhase ? "Chargé de commercialisation" : "Chargé de projet";
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(ev.id, e)}
      onClick={() => onOpen(ev)}
      data-testid={`event-card-${ev.id}`}
      className="fade-up group cursor-pointer bg-card border border-border/60 rounded-xl p-4 shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-transform active:scale-[0.98]"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      {ev.status_label && (
        <div className="mb-2">
          <Badge
            variant="outline"
            className={`rounded-full text-[10px] px-2 py-0.5 border ${STATUS_STYLE[ev.status_key] || ""}`}
            data-testid={`event-card-status-${ev.id}`}
          >
            {ev.status_label}
          </Badge>
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Client</div>
      <div className="font-display font-semibold leading-tight text-lg break-words line-clamp-2" data-testid={`event-card-client-${ev.id}`}>
        {ev.client || <span className="text-muted-foreground italic">—</span>}
      </div>
      <div className="flex items-center gap-1.5 mt-2 text-sm tabular-nums font-medium" style={{ color: accent }} data-testid={`event-card-date-${ev.id}`}>
        <CalendarIcon className="h-3.5 w-3.5" />
        {ev.start_date ? fmtFR(ev.start_date) : <span className="text-muted-foreground italic font-normal">date à définir</span>}
      </div>
      {ev.name && (
        <div className="text-xs text-muted-foreground mt-2 border-t border-border/40 pt-2 break-words line-clamp-2" title={ev.name}>
          {ev.name}
        </div>
      )}
      {isCancelled && (
        <div className="mt-2 rounded-lg border border-red-500/50 bg-red-500/15 px-2 py-1.5 text-[11px]" data-testid={`event-card-refusal-${ev.id}`}>
          <div className="font-bold text-red-200 uppercase tracking-wider text-[9px]">
            Refusé{refusedAt ? ` le ${fmtFR(refusedAt)}` : ""}
          </div>
          {refusedReason && (
            <div className="text-red-200 italic mt-0.5 line-clamp-2" title={refusedReason}>« {refusedReason} »</div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-muted-foreground">
        {ev.location && (
          <div className="flex items-center gap-1 min-w-0"><MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{ev.location}</span></div>
        )}
        {ev.budget > 0 && (
          <div className="ml-auto font-semibold text-foreground tabular-nums shrink-0">
            {Number(ev.budget).toLocaleString("fr-FR")} € {ev.budget_type || "HT"}
          </div>
        )}
      </div>
      {leadName && (
        <div className="mt-3">
          <div className="text-[9px] uppercase tracking-widest font-bold mb-0.5" style={{ color: accent }} data-testid={`event-card-lead-label-${ev.id}`}>
            {leadLabel}
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium" style={{ background: accent + "22", color: accent }}>
              {leadName?.[0]?.toUpperCase()}
            </div>
            <span className="text-foreground font-semibold truncate">{leadName}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [places, setPlaces] = useState([]);
  const [q, setQ] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("kanban");
  // Filtres Kanban : mois/année sur start_date + chargé de commercialisation + chargé de projet
  const [monthFilter, setMonthFilter] = useState("all");      // "all" | "YYYY-MM"
  const [yearFilter, setYearFilter] = useState("all");        // "all" | "YYYY"
  const [commLeadFilter, setCommLeadFilter] = useState("all"); // "all" | user_id | "none"
  const [projLeadFilter, setProjLeadFilter] = useState("all"); // idem

  const load = async () => {
    try {
      const [{ data: evs }, usersRes, clientsRes, placesRes] = await Promise.all([
        api.get("/events", { params: q ? { q } : {} }),
        api.get("/users/lookup").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/places").catch(() => ({ data: [] })),
      ]);
      setEvents(evs);
      setUsers(usersRes.data);
      setClients(clientsRes.data || []);
      setPlaces(placesRes.data || []);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  // Liste filtrée par mois/année/leads utilisée pour Kanban ET Liste
  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (monthFilter !== "all") {
        if (!e.start_date || e.start_date.slice(0, 7) !== monthFilter) return false;
      }
      if (yearFilter !== "all") {
        if (!e.start_date || e.start_date.slice(0, 4) !== yearFilter) return false;
      }
      if (commLeadFilter !== "all") {
        if (commLeadFilter === "none" ? e.commercial_lead_id : e.commercial_lead_id !== commLeadFilter) return false;
      }
      if (projLeadFilter !== "all") {
        if (projLeadFilter === "none" ? e.project_lead_id : e.project_lead_id !== projLeadFilter) return false;
      }
      return true;
    });
  }, [events, monthFilter, yearFilter, commLeadFilter, projLeadFilter]);

  const grouped = useMemo(() => {
    const g = { commercialisation: [], production: [], postprod: [], cancelled: [], archived: [] };
    filteredEvents.forEach(e => { (g[e.phase] || g.commercialisation).push(e); });
    return g;
  }, [filteredEvents]);

  const filteredList = useMemo(() => {
    return phaseFilter === "all" ? filteredEvents : filteredEvents.filter(e => e.phase === phaseFilter);
  }, [filteredEvents, phaseFilter]);

  // Options mois disponibles (dérivées des dates existantes)
  const monthOptions = useMemo(() => {
    const set = new Set();
    events.forEach(e => { if (e.start_date) set.add(e.start_date.slice(0, 7)); });
    return Array.from(set).sort().reverse();
  }, [events]);
  const yearOptions = useMemo(() => {
    const set = new Set();
    events.forEach(e => { if (e.start_date) set.add(e.start_date.slice(0, 4)); });
    return Array.from(set).sort().reverse();
  }, [events]);
  const FR_MONTHS_SHORT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const fmtMonth = (ym) => { const [y, m] = ym.split("-"); return `${FR_MONTHS_SHORT[parseInt(m, 10) - 1]} ${y}`; };
  const resetFilters = () => { setMonthFilter("all"); setYearFilter("all"); setCommLeadFilter("all"); setProjLeadFilter("all"); };
  const hasActiveFilters = monthFilter !== "all" || yearFilter !== "all" || commLeadFilter !== "all" || projLeadFilter !== "all";

  const openNew = () => {
    setForm({ ...EMPTY_EVENT, demande: { ...EMPTY_EVENT.demande }, brief: { ...EMPTY_EVENT.brief }, targeting: { ...EMPTY_EVENT.targeting }, ideas: { ...EMPTY_EVENT.ideas } });
    setEditingId(null);
    setDialogOpen(true);
  };
  const openEdit = (ev) => {
    setForm({
      ...EMPTY_EVENT,
      ...ev,
      referent_id: ev.referent_id || "",
      demande: { ...EMPTY_EVENT.demande, ...(ev.demande || {}) },
      brief: (() => {
        const b = { ...EMPTY_EVENT.brief, ...(ev.brief || {}) };
        // Rétro-compat : si l'ancien contact_full_name existe mais pas prénom/nom, on scinde.
        if (!b.contact_first_name && !b.contact_last_name && b.contact_full_name) {
          const parts = b.contact_full_name.trim().split(/\s+/);
          b.contact_first_name = parts.shift() || "";
          b.contact_last_name = parts.join(" ");
        }
        return b;
      })(),
      targeting: { ...EMPTY_EVENT.targeting, ...(ev.targeting || {}) },
      ideas: { ...EMPTY_EVENT.ideas, ...(ev.ideas || {}) },
      checkpoints: ev.checkpoints || {},
    });
    setEditingId(ev.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom de l'événement est obligatoire"); return; }
    try {
      // 1) Auto-création du client si l'entreprise saisie n'existe pas déjà (match case-insensitive)
      const entrepriseName = (form.brief?.entreprise || form.client || "").trim();
      if (entrepriseName) {
        const exists = clients.find(c => (c.name || "").toLowerCase() === entrepriseName.toLowerCase());
        if (!exists) {
          try {
            const { data: newClient } = await api.post("/clients", {
              name: entrepriseName,
              kind: "company",
              contact_first_name: form.brief?.contact_first_name || "",
              contact_last_name: form.brief?.contact_last_name || "",
              contact_role: form.brief?.position || "",
              contact_email: form.brief?.email || "",
              contact_phone: form.brief?.phone || "",
            });
            setClients(prev => [...prev, newClient]);
            toast.success(`Client "${entrepriseName}" créé automatiquement`);
          } catch (err) { /* création best-effort */ }
        }
      }
      // 2) Auto-création / ré-utilisation du lieu si un lieu a été saisi
      if ((form.location || "").trim()) {
        try {
          await api.post("/places/ensure", {
            name: form.location.trim(),
            address: form.address || "",
            city: form._place_city || "",
            postal_code: form._place_postal_code || "",
            country: form._place_country || "France",
            lat: form._place_lat || null,
            lng: form._place_lng || null,
            google_place_id: form._place_google_id || "",
            spaces: (form.space || "").trim() ? [form.space.trim()] : [],
          });
        } catch (err) { /* best-effort */ }
      }
      // 3) Reconstitue contact_full_name à partir de prénom + nom pour rétro-compatibilité
      const fullName = [form.brief?.contact_first_name, form.brief?.contact_last_name].filter(Boolean).join(" ").trim();
      const payload = {
        ...form,
        budget: parseFloat(form.budget) || 0,
        referent_id: form.referent_id || null,
        brief: {
          ...form.brief,
          contact_full_name: fullName || form.brief?.contact_full_name || "",
        },
        demande: {
          ...form.demande,
          received_by_id: form.demande?.received_by_id || null,
          source: form.demande?.source || null,
          decision_date: form.demande?.decision_date || null,
          quote_request_date: form.demande?.quote_request_date || null,
        },
      };
      // Retire les champs "internes" _place_* avant envoi
      delete payload._place_city; delete payload._place_postal_code; delete payload._place_country;
      delete payload._place_lat; delete payload._place_lng; delete payload._place_google_id;
      delete payload._place_id; delete payload._place_spaces;
      if (editingId) {
        await api.patch(`/events/${editingId}`, payload);
        toast.success("Événement modifié");
      } else {
        await api.post("/events", payload);
        toast.success("Événement créé dans « Commercialisation »");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const remove = async (ev) => {
    if (!confirm(`Supprimer "${ev.name}" ?`)) return;
    try { await api.delete(`/events/${ev.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const onDragStart = (id, e) => { e.dataTransfer.setData("text/eid", id); };
  const onDrop = async (phase, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/eid");
    if (!id) return;
    try {
      await api.patch(`/events/${id}/phase`, { phase });
      setEvents(evs => evs.map(x => x.id === id ? { ...x, phase } : x));
      toast.success(`Déplacé vers ${PHASES.find(p => p.v === phase)?.label}`);
    } catch (err) { toast.error(extractError(err)); }
  };

  const setDemande = (patch) => setForm(f => ({ ...f, demande: { ...f.demande, ...patch } }));
  const setBrief   = (patch) => setForm(f => ({ ...f, brief:   { ...f.brief,   ...patch } }));
  const setTarget  = (patch) => setForm(f => ({ ...f, targeting: { ...f.targeting, ...patch } }));
  const setIdeas   = (patch) => setForm(f => ({ ...f, ideas:   { ...f.ideas,   ...patch } }));

  return (
    <div className="space-y-6" data-testid="events-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Événements</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Pipeline & projets</h1>
          <p className="text-sm text-muted-foreground mt-1">Suivez chaque événement de la commercialisation à la post-prod.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher..." className="pl-9 h-10 w-56 bg-muted" data-testid="events-search" />
          </div>
          <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" onClick={openNew} data-testid="add-event-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouvel événement
          </Button>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted">
          <TabsTrigger value="kanban" data-testid="tab-kanban"><Kanban className="h-4 w-4 mr-2" /> Kanban</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list"><List className="h-4 w-4 mr-2" /> Liste</TabsTrigger>
        </TabsList>

        {/* Barre de filtres Kanban : mois / année / chargé com / chargé projet */}
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border/50 bg-muted px-3 py-2" data-testid="events-filters">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Filtres</span>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="h-8 w-40 rounded-full" data-testid="filter-month"><SelectValue placeholder="Mois" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les mois</SelectItem>
              {monthOptions.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="h-8 w-28 rounded-full" data-testid="filter-year"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes années</SelectItem>
              {yearOptions.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={commLeadFilter} onValueChange={setCommLeadFilter}>
            <SelectTrigger className="h-8 w-56 rounded-full" data-testid="filter-comm-lead"><SelectValue placeholder="Chargé de commercialisation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous — Chargé de com.</SelectItem>
              <SelectItem value="none">Non attribué</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projLeadFilter} onValueChange={setProjLeadFilter}>
            <SelectTrigger className="h-8 w-56 rounded-full" data-testid="filter-proj-lead"><SelectValue placeholder="Chargé de projet" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous — Chargé de projet</SelectItem>
              <SelectItem value="none">Non attribué</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={resetFilters} data-testid="filter-reset">Réinitialiser</Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">{filteredEvents.length} / {events.length}</span>
        </div>

        <TabsContent value="kanban" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {PHASES.filter(p => p.v !== "archived").map((p) => (
              <div
                key={p.v}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(p.v, e)}
                className={`rounded-2xl border p-3 min-h-[400px] flex flex-col ${p.colStyle}`}
                data-testid={`column-${p.v}`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`${p.color} rounded-full border font-semibold`}>{p.label}</Badge>
                    <span className="text-sm text-muted-foreground tabular-nums">{grouped[p.v]?.length || 0}</span>
                  </div>
                </div>
                <div className="space-y-3 flex-1">
                  {(grouped[p.v] || []).map(ev => (
                    <EventCard key={ev.id} ev={ev} onOpen={(e) => navigate(`/evenements/${e.id}`)} onDragStart={onDragStart} />
                  ))}
                  {(grouped[p.v] || []).length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-8 border-2 border-dashed border-border/60 rounded-xl">
                      Aucun événement — glissez une carte ici
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {grouped.archived?.length > 0 && (
            <details className="mt-6">
              <summary className="text-sm text-muted-foreground cursor-pointer">Archivés ({grouped.archived.length})</summary>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 opacity-70">
                {grouped.archived.map(ev => <EventCard key={ev.id} ev={ev} onOpen={(e) => navigate(`/evenements/${e.id}`)} onDragStart={onDragStart} />)}
              </div>
            </details>
          )}
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phase</Label>
                <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                  <SelectTrigger className="w-56 h-9 rounded-full" data-testid="phase-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {PHASES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-auto">{filteredList.length} événement(s)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="py-3 px-3 font-medium">Client</th>
                      <th className="py-3 px-3 font-medium">Événement</th>
                      <th className="py-3 px-3 font-medium">Date</th>
                      <th className="py-3 px-3 font-medium">Lieu</th>
                      <th className="py-3 px-3 font-medium">Phase</th>
                      <th className="py-3 px-3 font-medium text-right">Budget</th>
                      <th className="py-3 px-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.length === 0 && (
                      <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">Aucun événement</td></tr>
                    )}
                    {filteredList.map(ev => {
                      const p = PHASES.find(x => x.v === ev.phase) || PHASES[0];
                      return (
                        <tr key={ev.id} className="border-b border-border/40 hover:bg-muted fade-up" data-testid={`event-row-${ev.id}`}>
                          <td className="py-3 px-3 font-medium cursor-pointer" onClick={() => navigate(`/evenements/${ev.id}`)}>{ev.client || "—"}</td>
                          <td className="py-3 px-3 text-muted-foreground">{ev.name}</td>
                          <td className="py-3 px-3 text-muted-foreground tabular-nums">{ev.start_date ? fmtFR(ev.start_date) : "—"}</td>
                          <td className="py-3 px-3 text-muted-foreground">{ev.location || "—"}</td>
                          <td className="py-3 px-3"><Badge className={`${p.color} rounded-full border`}>{p.label}</Badge></td>
                          <td className="py-3 px-3 text-right tabular-nums">{ev.budget ? `${Number(ev.budget).toLocaleString("fr-FR")} € ${ev.budget_type || "HT"}` : "—"}</td>
                          <td className="py-3 px-3 text-right">
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/evenements/${ev.id}`)} data-testid={`open-event-${ev.id}`}><ExternalLinkIcon className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => remove(ev)} data-testid={`delete-event-${ev.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand tracking-tight text-2xl">
              {editingId ? "Fiche événement" : "Nouvel événement"}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              L'événement sera créé dans la colonne <strong>Commercialisation</strong> du Kanban.
            </p>
          </DialogHeader>

          <Accordion type="multiple" defaultValue={["demande", "info"]} className="w-full">
            {/* ---- DEMANDE (en haut) ---- */}
            <AccordionItem value="demande" className="border rounded-xl px-4 mb-3 bg-accent/20 border-accent/60">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <Inbox className="h-4 w-4 text-primary" /> Demande
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Demande reçue par</Label>
                    <Select
                      value={form.demande?.received_by_id || "none"}
                      onValueChange={v => setDemande({ received_by_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="demande-received-by"><SelectValue placeholder="Membre de l'équipe" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Origine de la demande</Label>
                    <Select
                      value={form.demande?.source || "none"}
                      onValueChange={v => setDemande({ source: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="demande-source"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {DEMAND_SOURCES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date de prise de décision</Label>
                    <Input
                      type="date"
                      value={form.demande?.decision_date || ""}
                      onChange={e => setDemande({ decision_date: e.target.value })}
                      data-testid="demande-decision-date"
                    />
                  </div>
                  <div>
                    <Label>Commentaire (prise de décision)</Label>
                    <Input
                      value={form.demande?.decision_comment || ""}
                      onChange={e => setDemande({ decision_comment: e.target.value })}
                      placeholder="Ex : décision d'ici fin de mois"
                      data-testid="demande-decision-comment"
                    />
                  </div>
                  <div>
                    <Label>Date de demande de remise du devis</Label>
                    <Input
                      type="date"
                      value={form.demande?.quote_request_date || ""}
                      onChange={e => setDemande({ quote_request_date: e.target.value })}
                      data-testid="demande-quote-date"
                    />
                  </div>
                  <div>
                    <Label>Commentaire (remise du devis)</Label>
                    <Input
                      value={form.demande?.quote_request_comment || ""}
                      onChange={e => setDemande({ quote_request_comment: e.target.value })}
                      placeholder="Ex : devis attendu sous 8 jours"
                      data-testid="demande-quote-comment"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- BRIEF CLIENT ---- */}
            <AccordionItem value="brief" className="border rounded-xl px-4 mb-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <Building2 className="h-4 w-4 text-primary" /> Brief client
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Prénom du contact *</Label>
                    <Input
                      value={form.brief?.contact_first_name || ""}
                      onChange={e => setBrief({ contact_first_name: e.target.value })}
                      data-testid="brief-contact-first-name"
                    />
                  </div>
                  <div>
                    <Label>Nom du contact *</Label>
                    <Input
                      value={form.brief?.contact_last_name || ""}
                      onChange={e => setBrief({ contact_last_name: e.target.value })}
                      data-testid="brief-contact-last-name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Entreprise / asso / autres *</Label>
                    <AutocompleteInput
                      items={clients}
                      getLabel={(c) => c.name || ""}
                      getSubtitle={(c) => [c.contact_first_name, c.contact_last_name].filter(Boolean).join(" ")}
                      value={form.brief?.entreprise || form.client || ""}
                      onChange={(v) => { setBrief({ entreprise: v }); setForm(f => ({ ...f, client: v })); }}
                      onSelect={(c) => {
                        setBrief({
                          entreprise: c.name,
                          contact_first_name: form.brief?.contact_first_name || c.contact_first_name || "",
                          contact_last_name: form.brief?.contact_last_name || c.contact_last_name || "",
                          email: form.brief?.email || c.contact_email || "",
                          phone: form.brief?.phone || c.contact_phone || "",
                          position: form.brief?.position || c.contact_role || "",
                        });
                        setForm(f => ({ ...f, client: c.name }));
                      }}
                      placeholder="Tapez au moins 2 lettres…"
                      dataTestId="brief-entreprise"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1">Sélectionnez un client existant ou saisissez un nouveau nom : il sera créé automatiquement à l'enregistrement.</div>
                  </div>
                  <div>
                    <Label>Poste dans l'entreprise</Label>
                    <Input value={form.brief?.position || ""} onChange={e => setBrief({ position: e.target.value })} data-testid="brief-position" />
                  </div>
                  <div>
                    <Label>Mail *</Label>
                    <Input type="email" value={form.brief?.email || ""} onChange={e => setBrief({ email: e.target.value })} data-testid="brief-email" />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input value={form.brief?.phone || ""} onChange={e => setBrief({ phone: e.target.value })} data-testid="brief-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Autres…</Label>
                    <Input value={form.brief?.brief_other || ""} onChange={e => setBrief({ brief_other: e.target.value })} data-testid="brief-other" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- INFOS ÉVÉNEMENT ---- */}
            <AccordionItem value="info" className="border rounded-xl px-4 mb-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <ClipboardList className="h-4 w-4 text-primary" /> Informations événement
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="md:col-span-2">
                    <Label>Nom de l'événement *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="event-name" />
                  </div>
                  <div>
                    <Label>Date de début *</Label>
                    <Input type="date" value={form.start_date || ""} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="event-start" />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input type="date" value={form.end_date || ""} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="event-end" />
                  </div>
                  <div>
                    <Label>Horaire de début</Label>
                    <Input value={form.horaire || ""} onChange={e => setForm({ ...form, horaire: e.target.value })} placeholder="Ex : 09h" data-testid="event-horaire" />
                  </div>
                  <div>
                    <Label>Horaire de fin</Label>
                    <Input value={form.horaire_end || ""} onChange={e => setForm({ ...form, horaire_end: e.target.value })} placeholder="Ex : 18h" data-testid="event-horaire-end" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Lieu (Google Maps + carnet)</Label>
                    <GooglePlacePicker
                      existingPlaces={places}
                      value={{
                        name: form.location,
                        address: form.address,
                        city: form._place_city, postal_code: form._place_postal_code, country: form._place_country,
                        lat: form._place_lat, lng: form._place_lng, google_place_id: form._place_google_id,
                      }}
                      onPick={(p) => setForm(f => ({
                        ...f,
                        location: p.name || f.location,
                        address: p.address || f.address,
                        _place_id: p.id || f._place_id,
                        _place_spaces: p.spaces || f._place_spaces || [],
                        _place_city: p.city, _place_postal_code: p.postal_code, _place_country: p.country,
                        _place_lat: p.lat, _place_lng: p.lng, _place_google_id: p.google_place_id,
                      }))}
                      label=""
                      dataTestId="event-location"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1">Sélectionnez un lieu existant ou recherchez sur Google Maps — un nouveau lieu sera ajouté au carnet automatiquement.</div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Espace / salle</Label>
                    <AutocompleteInput
                      items={(() => {
                        // Suggestions basées sur le lieu sélectionné, sinon aucun.
                        const p = places.find(x => x.name === form.location) || {};
                        return (p.spaces || []).map((s) => ({ id: s, name: s }));
                      })()}
                      getLabel={(x) => x.name || ""}
                      value={form.space || ""}
                      onChange={(v) => setForm(f => ({ ...f, space: v }))}
                      onSelect={(x) => setForm(f => ({ ...f, space: x.name }))}
                      minChars={1}
                      placeholder="Ex : Grande salle, Terrasse… (facultatif)"
                      dataTestId="event-space"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1">Si l'espace n'existe pas encore pour ce lieu, il sera ajouté à la fiche du lieu automatiquement.</div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Adresse complète</Label>
                    <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="event-address" />
                  </div>
                  <div>
                    <Label>Chargé de Commercialisation</Label>
                    <Select value={form.referent_id || "none"} onValueChange={v => setForm({ ...form, referent_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="event-referent"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phase</Label>
                    <Select value={form.phase} onValueChange={v => setForm({ ...form, phase: v })}>
                      <SelectTrigger data-testid="event-phase"><SelectValue /></SelectTrigger>
                      <SelectContent>{PHASES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- BUDGET ---- */}
            <AccordionItem value="budget" className="border rounded-xl px-4 mb-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <span className="text-primary text-lg">€</span> Budget
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  <div className="md:col-span-2">
                    <Label>Budget</Label>
                    <Input type="number" min="0" step="0.01" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} data-testid="event-budget" />
                  </div>
                  <div>
                    <Label>HT / TTC</Label>
                    <Select value={form.budget_type || "HT"} onValueChange={v => setForm({ ...form, budget_type: v })}>
                      <SelectTrigger data-testid="event-budget-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HT">HT</SelectItem>
                        <SelectItem value="TTC">TTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label>Commentaires</Label>
                    <Textarea
                      rows={3}
                      value={form.budget_comment ?? ""}
                      onChange={e => setForm({ ...form, budget_comment: e.target.value })}
                      placeholder="Notes / précisions sur le budget (conditions client, options, remises…)"
                      data-testid="event-budget-comment"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- CIBLAGE ---- */}
            <AccordionItem value="ciblage" className="border rounded-xl px-4 mb-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <Target className="h-4 w-4 text-primary" /> Ciblage
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Pour qui ? *</Label>
                    <Input value={form.targeting?.for_who || ""} onChange={e => setTarget({ for_who: e.target.value })} placeholder="Salariés, cadres, patron, famille…" data-testid="target-for-who" />
                  </div>
                  <div>
                    <Label>Quel âge ?</Label>
                    <Input value={form.targeting?.age || ""} onChange={e => setTarget({ age: e.target.value })} placeholder="Enfant, ado, adulte, senior…" data-testid="target-age" />
                  </div>
                  <div>
                    <Label>Majoritairement pour quel genre ?</Label>
                    <Input value={form.targeting?.gender || ""} onChange={e => setTarget({ gender: e.target.value })} placeholder="Homme / Femme" data-testid="target-gender" />
                  </div>
                  <div>
                    <Label>Dans quel but ? *</Label>
                    <Input value={form.targeting?.purpose || ""} onChange={e => setTarget({ purpose: e.target.value })} placeholder="Divertissement, pédagogie…" data-testid="target-purpose" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Que font-ils avant / après ?</Label>
                    <Textarea rows={2} value={form.targeting?.before_after || ""} onChange={e => setTarget({ before_after: e.target.value })} placeholder="Déroulement de la journée / week-end…" data-testid="target-before-after" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Événements similaires précédents ?</Label>
                    <Textarea rows={2} value={form.targeting?.previous_events || ""} onChange={e => setTarget({ previous_events: e.target.value })} placeholder="Année précédente, autre…" data-testid="target-previous" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Autres…</Label>
                    <Input value={form.targeting?.other || ""} onChange={e => setTarget({ other: e.target.value })} data-testid="target-other" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- RECUEIL IDÉES ---- */}
            <AccordionItem value="ideas" className="border rounded-xl px-4 mb-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <Lightbulb className="h-4 w-4 text-primary" /> Recueil idées client
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Ce qu'il aimerait</Label>
                    <Textarea rows={3} value={form.ideas?.wants || ""} onChange={e => setIdeas({ wants: e.target.value })} data-testid="ideas-wants" />
                  </div>
                  <div>
                    <Label>Ce qu'il ne veut pas</Label>
                    <Textarea rows={3} value={form.ideas?.dont_wants || ""} onChange={e => setIdeas({ dont_wants: e.target.value })} data-testid="ideas-dont-wants" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Autres…</Label>
                    <Input value={form.ideas?.other || ""} onChange={e => setIdeas({ other: e.target.value })} data-testid="ideas-other" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ---- COMMENTAIRES ---- */}
            <AccordionItem value="comm" className="border rounded-xl px-4">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 font-brand tracking-wide">
                  <MessageSquare className="h-4 w-4 text-primary" /> Commentaires
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <Label>Infos supp.</Label>
                  <Textarea rows={4} value={form.infos_supp || form.description || ""} onChange={e => setForm({ ...form, infos_supp: e.target.value, description: e.target.value })} data-testid="event-infos-supp" />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <DialogFooter className="mt-4">
            {editingId && (
              <>
                <Button
                  variant="outline"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
                  onClick={() => {
                    const url = buildGoogleCalendarUrl({ ...form, referent_name: events.find(x => x.id === editingId)?.referent_name });
                    if (!url) { toast.error("Renseignez une date de début pour l'ajouter à l'agenda"); return; }
                    window.open(url, "_blank", "noopener");
                  }}
                  data-testid="gcal-btn"
                >
                  <CalendarPlus className="h-4 w-4 mr-2" /> Ajouter à Google Agenda
                </Button>
                <Button variant="ghost" className="text-destructive mr-auto" onClick={() => { const ev = events.find(x => x.id === editingId); setDialogOpen(false); remove(ev); }}>
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={save} data-testid="confirm-event-btn">
              {editingId ? "Enregistrer" : "Créer l'événement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
